import { isNotApproved, isApproved, isNotConfirmed, isConfirmed } from '@sporkbytes/object-utils';
import { getValidDate, isWithinHours } from '@sporkbytes/time-utils';
import { isPast } from 'date-fns';
import { confirmationWindowLengthInHours } from '@sporkbytes/config';
import { roundCurrency, calculatePercent, getPercent } from '@sporkbytes/math-utils';
import Money from '@sporkbytes/money';
import { foodSubtotal as foodSubtotal$1, getIncludedContacts as getIncludedContacts$1, getIncludedItems as getIncludedItems$1, totalReceivedByPartner, sporkCommission as sporkCommission$1 } from '@sporkbytes/purchase-order-utils';
import uniqBy from 'lodash.uniqby';
import { itemTotalDiscountActual } from '@sporkbytes/purchase-order-menu-item-utils';

function _typeof(o) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) {
    return typeof o;
  } : function (o) {
    return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
  }, _typeof(o);
}
function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}
function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}
function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}
function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function associatedMealProposalInCorrectState(mealProposal) {
  var additionalStatuses = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  return !isPast(getValidDate(mealProposal === null || mealProposal === void 0 ? void 0 : mealProposal.deliveryDate)) && ['In Progress'].concat(_toConsumableArray(additionalStatuses)).includes(mealProposal === null || mealProposal === void 0 ? void 0 : mealProposal.status);
}

/**
 * @description Given `mealOption`, determine if it can be approved.
 * @param {object} mealOption - The meal option and its associated meal proposal to determine if it can be approved.
 * @return {boolean} Whether the given meal option can be approved.
 */
function canApprove(mealOption) {
  var _mealOption$MealPropo, _mealOption$MealPropo2;
  return Array.isArray(mealOption === null || mealOption === void 0 ? void 0 : (_mealOption$MealPropo = mealOption.MealProposal) === null || _mealOption$MealPropo === void 0 ? void 0 : _mealOption$MealPropo.MealOptions) && (mealOption === null || mealOption === void 0 ? void 0 : (_mealOption$MealPropo2 = mealOption.MealProposal) === null || _mealOption$MealPropo2 === void 0 ? void 0 : _mealOption$MealPropo2.MealOptions.every(isNotApproved)) && associatedMealProposalInCorrectState(mealOption.MealProposal);
}

/**
 * @description Given `mealOption`, determine if it has been approved by all partners or not.
 * @param {object} mealOption - The meal option to be determined as approved by all partners or not.
 * @return {boolean} Whether the given meal option has been approved by all partners or not.
 */
function partnersHaveApproved(mealOption) {
  if (mealOption != null && Array.isArray(mealOption.PurchaseOrders)) {
    return mealOption.PurchaseOrders.every(isApproved);
  }
  return false;
}

/**
 * @description Given `mealOption`, determine if it can be confirmed.
 * @param {object} mealOption - The meal option and its associated meal proposal to determine if it can be confirmed.
 * @return {boolean} Whether the given meal option can be confirmed.
 */
function canConfirm(mealOption) {
  var _mealOption$MealPropo;
  return isApproved(mealOption) && isNotConfirmed(mealOption) && partnersHaveApproved(mealOption) && associatedMealProposalInCorrectState(mealOption.MealProposal) && isWithinHours(mealOption === null || mealOption === void 0 ? void 0 : (_mealOption$MealPropo = mealOption.MealProposal) === null || _mealOption$MealPropo === void 0 ? void 0 : _mealOption$MealPropo.deliveryDate, new Date(), confirmationWindowLengthInHours.client, true);
}

/**
 * @description Given `mealOption`, determine if it can be deleted.
 * @param {object} mealOption - The meal option and its associated meal proposal to determine if it can be deleted.
 * @return {boolean} Whether the given meal option can be deleted.
 */
function canDelete(mealOption) {
  return isNotApproved(mealOption) && associatedMealProposalInCorrectState(mealOption.MealProposal, ['Draft']);
}

/**
 * @description Given `mealOption`, determine if it can be disapproved.
 * @param {object} mealOption - The meal option and its associated meal proposal to determine if it can be disapproved.
 * @return {boolean} Whether the given meal option can be disapproved.
 */
function canDisapprove(mealOption) {
  return isApproved(mealOption) && isNotConfirmed(mealOption) && associatedMealProposalInCorrectState(mealOption.MealProposal);
}

/**
 * @description Given `mealOption`, determine if it can have its confirmation undone.
 * @param {object} mealOption - The meal option and its associated meal proposal to determine if it can have its confirmation undone.
 * @return {boolean} Whether the given meal option can have its confirmation undone.
 */
function canUndoConfirmation(mealOption) {
  return isConfirmed(mealOption) && associatedMealProposalInCorrectState(mealOption.MealProposal);
}

/**
 * @description Given a `mealOption` and optional `amountType`, calculate the cost of the food in that meal option.
 * @param {object} mealOption - The meal option for which we calculate the food subtotal.
 * @param {string} [amountType = 'costToClient'] - The amount being retrieved.  Valid options are `costToClient` and `paidToPartner`.
 * @return {number} The currency-rounded food subtotal amount for the meal option, either the amount the client pays (if `amountType` is `costToClient`) or the amount given to the partner (if `amountType` is `paidToPartner`).
 */
function foodSubtotal(mealOption) {
  var amountType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'costToClient';
  if (Array.isArray(mealOption.PurchaseOrders)) {
    return +mealOption.PurchaseOrders.reduce(function (total, purchaseOrder) {
      return total.add(foodSubtotal$1(purchaseOrder, amountType));
    }, new Money(0));
  }
  return 0;
}

/**
 * @description Given a `feeObject`, `mealOption`, and related `mealProposal.headCount`, calculate the fee amount for the `mealOption` before discounts are applied.
 * @param {object} feeObject - The object of the fee you're calculating.
 * @param {object} mealOption - The meal option for which we calculate the given fee.
 * @param {number} headCount - The head count for the meal option's related meal proposal.  This is only required if `feeObject.type` is `per person`.
 * @return {number} The fee amount before discounts are applied.
 */
function getFeeBeforeDiscount(feeObject, mealOption, headCount) {
  var amount = feeObject.amount;
  switch (feeObject.type) {
    case 'percent':
      return roundCurrency(calculatePercent(foodSubtotal(mealOption, 'costToClient'), amount));
    case 'per person':
      return +new Money(amount).multiply(headCount);
    case 'flat':
    default:
      return roundCurrency(amount);
  }
}

/**
 * @description Given a `feeObject`, `mealOption`, and related `mealProposal.headCount`, calculate the discount amount for the given `mealOption` and `feeObject`.  **NOTE** we do not round this as a currency due to how Xero does rounding.  For more information, see [this Asana task]{@link https://app.asana.com/0/525815450072863/610544412664470}.
 * @param {object} feeObject - The object of the fee you're calculating.
 * @param {object} mealOption - The meal option for which we calculate the given fee discount.
 * @param {number} headCount - The head count for the meal option's related meal proposal.  This is only required if `feeObject.type` is `per person`.
 * @return {number} The amount the fee is discounted without rounding.
 */
function getFeeDiscount(feeObject, mealOption, headCount) {
  var feeAmount = getFeeBeforeDiscount(feeObject, mealOption, headCount);
  if (!!feeObject.discount) {
    return calculatePercent(feeAmount, feeObject.discount);
  }
  return 0;
}

/**
 * @description Given a `feeObject`, `mealOption`, and related `mealProposal.headCount`, calculate the fee amount for the `mealOption` after discounts are applied.
 * @param {object} feeObject - The object of the fee you're calculating.
 * @param {object} mealOption - The meal option for which we calculate the given fee.
 * @param {number} headCount - The head count for the meal option's related meal proposal.  This is only required if `feeObject.type` is `per person`.
 * @return {number} The fee amount after discounts are applied.
 */
function getFeeAfterDiscount(feeObject, mealOption, headCount) {
  var feeAmount = new Money(getFeeBeforeDiscount(feeObject, mealOption, headCount));
  var feeDiscount = getFeeDiscount(feeObject, mealOption, headCount);
  return +feeAmount.subtract(feeDiscount);
}

/**
 * @description Given a `mealOption` and related `mealProposal` properties, calculate the total amount the client pays before gratuity is added.
 * @param {object} mealOption - The meal option for which we calculate the total amount the client pays.
 * @param {object} mealProposal - Properties of the meal option's related meal proposal.
 * @param {array} mealProposal.MealProposalFees - The fees for the meal option's related meal proposal.
 * @param {number} mealProposal.headCount - The head count for the meal option's related client location.
 * @return {number} The currency-rounded total amount the client pays before gratuity is added.
 */
function totalClientPaysBeforeGratuity(mealOption, _ref) {
  var MealProposalFees = _ref.MealProposalFees,
    headCount = _ref.headCount;
  var totalFeesAfterDiscount = +MealProposalFees.reduce(function (total, fee) {
    return total.add(getFeeAfterDiscount(fee, mealOption, headCount));
  }, new Money(0));
  return +new Money(foodSubtotal(mealOption)).add(totalFeesAfterDiscount);
}

/**
 * @description Given a `mealOption` and related `mealProposal` properties, calculate the total amount the client pays, including gratuity.
 * @param {object} mealOption - The meal option for which we calculate the total amount the client pays.
 * @param {object} mealProposal - Properties of the meal option's related meal proposal.
 * @param {array} mealProposal.MealProposalFees - The fees for the meal option's related meal proposal.
 * @param {number} mealProposal.headCount - The head count for the meal option's related client location.
 * @return {number} The total amount the client pays, including gratuity.
 */
function totalClientPays(mealOption, _ref) {
  var MealProposalFees = _ref.MealProposalFees,
    headCount = _ref.headCount;
  if (mealOption.gratuity != null) {
    return +new Money(totalClientPaysBeforeGratuity(mealOption, {
      MealProposalFees: MealProposalFees,
      headCount: headCount
    })).add(mealOption.gratuity);
  }
  return totalClientPaysBeforeGratuity(mealOption, {
    MealProposalFees: MealProposalFees,
    headCount: headCount
  });
}

/**
 * @description Given a `mealOption` and related `mealProposal` properties, calculate the total amount the client pays per person, including gratuity.
 * @param {object} mealOption - The meal option for which we calculate the total amount the client pays per person.
 * @param {object} mealProposal - Properties of the meal option's related meal proposal.
 * @param {array} mealProposal.MealProposalFees - The fees for the meal option's related meal proposal.
 * @param {number} mealProposal.headCount - The head count for the meal option's related client location.
 * @return {number} The total amount the client pays per person, including gratuity.
 */
function costPerPerson(mealOption, _ref) {
  var MealProposalFees = _ref.MealProposalFees,
    headCount = _ref.headCount;
  return roundCurrency(totalClientPays(mealOption, {
    MealProposalFees: MealProposalFees,
    headCount: headCount
  }) / headCount);
}

/**
 * @description Given a `mealOption` and related `mealProposal` properties, calculate whether the total amount the client pays per person, including gratuity, is higher than their budget.
 * @param {object} mealOption - The meal option for which we calculate whether the total amount the client pays per person is too high.
 * @param {object} mealProposal - Properties of the meal option's related meal proposal.
 * @param {array} mealProposal.MealProposalFees - The fees for the meal option's related meal proposal.
 * @param {number} mealProposal.headCount - The head count for the meal option's related client location.
 * @param {string} mealProposal.budgetPerPerson - The head count for the meal option's related client location.  This is a string due to the way currencies are handled by the Angular plugin we use for currency inputs.
 * @return {number} Whether the total amount the client pays per person, including gratuity, is higher than their budget.
 */
function costPerPersonTooHigh(mealOption, _ref) {
  var MealProposalFees = _ref.MealProposalFees,
    headCount = _ref.headCount,
    budgetPerPerson = _ref.budgetPerPerson;
  return costPerPerson(mealOption, {
    MealProposalFees: MealProposalFees,
    headCount: headCount
  }) > parseFloat(budgetPerPerson);
}

/**
 * @description Given a `mealOptions` array, find the approved meal option in the array.
 * @param {array} mealOptions - The array of meals options for which we find the approved one.
 * @return {object} The approved meal option in `mealOptions`.
 */
function getClientApproved(mealOptions) {
  return Array.isArray(mealOptions) ? mealOptions.find(isApproved) : undefined;
}

/**
 * @description Given a `mealOptions` array, find the confirmed meal option in the array.
 * @param {array} mealOptions - The array of meals options for which we find the confirmed one.
 * @return {object} The confirmed meal option in `mealOptions`.
 */
function getClientConfirmed(mealOptions) {
  return Array.isArray(mealOptions) ? mealOptions.find(isConfirmed) : undefined;
}

/**
 * @description Given a `feeObject`, `mealOption`, and related `mealProposal.headCount`, calculate the actual discount amount for the given `mealOption` and `feeObject`.  **NOTE** this is rounded as a currency, but is **not necessarily equal to** `getFeeDiscount` with currency-rounding applied.
 * @param {object} feeObject - The object of the fee you're calculating.
 * @param {object} mealOption - The meal option for which we calculate the given fee discount.
 * @param {number} headCount - The head count for the meal option's related meal proposal.  This is only required if `feeObject.type` is `per person`.
 * @return {number} The actual amount the fee is discounted.
 */
function getFeeDiscountActual(feeObject, mealOption, headCount) {
  var feeAmountBeforeDiscount = new Money(getFeeBeforeDiscount(feeObject, mealOption, headCount));
  var feeAmountAfterDiscount = getFeeAfterDiscount(feeObject, mealOption, headCount);
  return +feeAmountBeforeDiscount.subtract(feeAmountAfterDiscount);
}

/**
 * @description Given a meal option object, retrieve the included contacts for all of the meal option's purchase orders.
 * @param {object} mealOption - The meal option object from which to retrieve the included contacts.
 * @return {array} The included contacts from the meal option's purchase orders.
 */
function getIncludedContacts(mealOption) {
  if (Array.isArray(mealOption.PurchaseOrders)) {
    var includedContacts = mealOption.PurchaseOrders.flatMap(getIncludedContacts$1);
    return uniqBy(includedContacts, function (contact) {
      return contact.id;
    });
  }
  return [];
}

/**
 * @description Given a meal option object, retrieve the included menu items.
 * @param {object} mealOption - The meal option object from which to retrieve the included menu items.
 * @return {array} The included menu items from the meal option.
 */
function getIncludedItems(mealOption) {
  return Array.isArray(mealOption.PurchaseOrders) ? mealOption.PurchaseOrders.flatMap(getIncludedItems$1) : [];
}

/**
 * @description Given a meal option object, retrieve the included menu items for the menu.
 * @param {object} mealOption - The meal option object from which to retrieve the included menu items for the menu.
 * @return {array} The included menu items from the meal option for the menu.
 */
function getIncludedItemsForMenu(mealOption) {
  return getIncludedItems(mealOption).filter(function (item) {
    return _typeof(item.PartnerMenuItem) === 'object' && item.PartnerMenuItem.visibleInMenu === true;
  });
}

/**
 * @description Given a meal option object or array of meal option objects, calculate the total received by all partners for the meal option(s).
 * @param {object|array} mealOptions - The meal option object or array of meal option objects for which we calculate the total received by all partners.
 * @return {number} The total amount of money received by all partners for the meal option.
 */
function totalReceivedByAllPartners(mealOptions) {
  if (Array.isArray(mealOptions)) {
    return +mealOptions.flatMap(function (mealOption) {
      return totalReceivedByAllPartners(mealOption);
    }).reduce(function (accumulator, value) {
      return accumulator.add(value);
    }, new Money(0));
  }
  if (Array.isArray(mealOptions.PurchaseOrders)) {
    return totalReceivedByPartner(mealOptions.PurchaseOrders);
  }
  return 0;
}

/**
 * @description Given a `mealOption` and related `mealProposal` properties, calculate the total amount received by Spork.
 * @param {object} mealOption - The meal option for which we calculate the total amount received by Spork.
 * @param {object} mealProposal - Properties of the meal option's related meal proposal.
 * @param {array} mealProposal.MealProposalFees - The fees for the meal option's related meal proposal.
 * @param {number} mealProposal.headCount - The head count for the meal option's related client location.
 * @return {number} The total amount received by Spork.
 */
function totalReceivedBySpork(mealOption, _ref) {
  var MealProposalFees = _ref.MealProposalFees,
    headCount = _ref.headCount;
  return +new Money(totalClientPays(mealOption, {
    MealProposalFees: MealProposalFees,
    headCount: headCount
  })).subtract(totalReceivedByAllPartners(mealOption));
}

/**
 * @description Given a `mealOption` and related `mealProposal` properties, calculate Spork's gross margin for the given `mealOption`.
 * @param {object} mealOption - The meal option for which we calculate the gross margin.
 * @param {object} mealProposal - Properties of the meal option's related meal proposal.
 * @param {array} mealProposal.MealProposalFees - The fees for the meal option's related meal proposal.
 * @param {number} mealProposal.headCount - The head count for the meal option's related client location.
 * @return {number} Spork's gross margin for the provided meal option.
 */
function grossMargin(mealOption, _ref) {
  var MealProposalFees = _ref.MealProposalFees,
    headCount = _ref.headCount;
  return getPercent(totalReceivedBySpork(mealOption, {
    MealProposalFees: MealProposalFees,
    headCount: headCount
  }), totalClientPays(mealOption, {
    MealProposalFees: MealProposalFees,
    headCount: headCount
  })) || 0;
}

/**
 * @description Given `mealOption`, determine if it has been confirmed by all partners or not.
 * @param {object} mealOption - The meal option to be determined as confirmed by all partners or not.
 * @return {boolean} Whether the given meal option has been confirmed by all partners or not.
 */
function partnersHaveConfirmed(mealOption) {
  if (mealOption != null && Array.isArray(mealOption.PurchaseOrders)) {
    return mealOption.PurchaseOrders.every(isConfirmed);
  }
  return false;
}

/**
 * @description Given a `mealOption`, determine Spork's commission.
 * @param {object} mealOption - The meal option for which we we calculate Spork's commission.
 * @return {number} Spork's commission for the meal option.
 */
function sporkCommission(mealOption) {
  return Array.isArray(mealOption.PurchaseOrders) ? +mealOption.PurchaseOrders.reduce(function (total, purchaseOrder) {
    return total.add(sporkCommission$1(purchaseOrder));
  }, new Money(0)) : 0;
}

/**
 * @description Given a `mealOption` and related `mealProposal` properties, calculate the actual total discount amount for the given `mealOption`.
 * @param {object} mealOption - The meal option for which we calculate the given total discount.
 * @param {object} mealProposal - Properties of the meal option's related meal proposal.
 * @param {array} mealProposal.MealProposalFees - The fees for the meal option's related meal proposal.
 * @param {number} mealProposal.headCount - The head count for the meal option's related client location.
 * @return {number} The actual total amount discounted for the meal option.
 */
function totalDiscount(mealOption, _ref) {
  var MealProposalFees = _ref.MealProposalFees,
    headCount = _ref.headCount;
  var includedItems = getIncludedItems(mealOption);
  var itemDiscounts = includedItems.reduce(function (discountSum, item) {
    return discountSum.add(itemTotalDiscountActual(item));
  }, new Money(0));
  var feeDiscounts = +MealProposalFees.reduce(function (total, fee) {
    return total.add(getFeeDiscountActual(fee, mealOption, headCount));
  }, new Money(0));
  return +itemDiscounts.add(feeDiscounts);
}

export { canApprove, canConfirm, canDelete, canDisapprove, canUndoConfirmation, costPerPerson, costPerPersonTooHigh, foodSubtotal, getClientApproved, getClientConfirmed, getFeeAfterDiscount, getFeeBeforeDiscount, getFeeDiscount, getFeeDiscountActual, getIncludedContacts, getIncludedItems, getIncludedItemsForMenu, grossMargin, partnersHaveApproved, partnersHaveConfirmed, sporkCommission, totalClientPays, totalClientPaysBeforeGratuity, totalDiscount, totalReceivedByAllPartners, totalReceivedBySpork };
